import { ADMIN_SESSION_MAX_HOURS_SETTING_KEY, DEFAULT_ADMIN_SESSION_MAX_HOURS } from "../types/mfa";

/**
 * The MCP server's fixed numbers and names (ADR 0009, sections 3, 4 and 9). One place, read by
 * the authorization server (`server/auth/mcp/`), the endpoint (`server/mcp/`), the consent and
 * management routes, and the runner's sweep (`./sweep.ts`). The CHECKs of platform migration
 * `mcp` spell the scope, profile and revoke-reason lists again; `shared/mcp/__tests__` keeps the
 * two in step.
 */

/** The platform feature flag (`features.flags.mcp`): off, `/mcp` and the OAuth endpoints answer 404. */
export const MCP_FLAG = "mcp";

/** Every access token starts with this; `/api/v2` and the WebSocket refuse a bearer that does. */
export const MCP_ACCESS_TOKEN_PREFIX = "skydata_mcp_";
/** Refresh tokens; distinct from the access prefix, so neither can stand for the other. */
export const MCP_REFRESH_TOKEN_PREFIX = "skydata_mcpr_";
/** Authorization codes, so a code pasted as a bearer is recognised and refused. */
export const MCP_CODE_PREFIX = "skydata_mcpc_";

/**
 * True for any value minted by the MCP authorization server. `/api/v2` and the WebSocket refuse
 * such a bearer and write `auth.token_rejected` (ADR 0009 section 2, layer 1).
 */
export function isMcpToken(value: string): boolean {
  return (
    value.startsWith(MCP_ACCESS_TOKEN_PREFIX) ||
    value.startsWith(MCP_REFRESH_TOKEN_PREFIX) ||
    value.startsWith(MCP_CODE_PREFIX)
  );
}

export const MCP_SCOPE_READ = "skydata.read";
export const MCP_SCOPE_WRITE = "skydata.write";
/** Listed in `scopes_supported` so claude.ai asks for a refresh token (ADR 0009 section 3). */
export const MCP_SCOPE_OFFLINE = "offline_access";
export const MCP_SCOPES = [MCP_SCOPE_READ, MCP_SCOPE_WRITE, MCP_SCOPE_OFFLINE] as const;
export type McpScope = (typeof MCP_SCOPES)[number];

export function isMcpScope(value: string): value is McpScope {
  return (MCP_SCOPES as readonly string[]).includes(value);
}

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/** An authorization code: single use, 5 minutes. */
export const MCP_CODE_TTL_MS = 5 * MINUTE_MS;
/** An access token: 1 hour. */
export const MCP_ACCESS_TOKEN_TTL_MS = HOUR_MS;
/** A refresh token dies 14 days after it was issued if nobody uses it. */
export const MCP_REFRESH_IDLE_TTL_MS = 14 * DAY_MS;
/** And never later than 30 days after consent: the grant's absolute limit. */
export const MCP_GRANT_ABSOLUTE_TTL_MS = 30 * DAY_MS;
/**
 * A rotated refresh token presented again within this window is a client retry, not a theft,
 * and does not revoke the grant (ADR 0009 section 4).
 */
export const MCP_REFRESH_REUSE_GRACE_MS = 30_000;
/** A registered client that no person approves within 24 hours is deleted. */
export const MCP_UNAPPROVED_CLIENT_TTL_MS = 24 * HOUR_MS;
/**
 * How long a revoked or expired grant's row stays for "Connected apps" and the access review
 * before the sweep deletes it. The audit trail keeps the issue and the revocation for good.
 */
export const MCP_ENDED_GRANT_KEEP_MS = 30 * DAY_MS;
/** The first approval for a client needs a sign-in no older than this (ADR 0009 section 3). */
export const MCP_FIRST_CONSENT_MAX_AUTH_AGE_MS = 5 * MINUTE_MS;

/**
 * The admin cap: an InstanceAdmin's or TenantAdmin's grant under the admin MFA modes is usable
 * for at most the admin session length after the consent sign-in. It is the existing platform
 * setting, not a new number; this is its key and its fallback.
 */
export const MCP_ADMIN_CAP_SETTING_KEY = ADMIN_SESSION_MAX_HOURS_SETTING_KEY;
export const MCP_ADMIN_CAP_DEFAULT_HOURS = DEFAULT_ADMIN_SESSION_MAX_HOURS;

/** Why a grant ended. The migration's `mcp_grants_revoked_reason_check` lists the same words. */
export const MCP_GRANT_REVOKE_REASONS = [
  "user",
  "admin",
  "revoke_all",
  "refresh_reuse",
  "code_reuse",
  "user_deactivated",
  "access_lost",
  "instance_disabled",
  "tenant_disabled",
  "issuer_removed",
  "switched_off",
  "reconsent",
] as const;
export type McpGrantRevokeReason = (typeof MCP_GRANT_REVOKE_REASONS)[number];

/** A tenant with no switch row: read on, write off until a TenantAdmin turns it on. */
export const MCP_TENANT_SWITCH_DEFAULTS = { readEnabled: true, writeEnabled: false } as const;
/** An instance with no switch row is on, as far as its tenant allows. */
export const MCP_INSTANCE_SWITCH_DEFAULT_ENABLED = true;

/** The MCP endpoint's path, outside `/api/v2` (ADR 0009 section 1). */
export const MCP_PATH = "/mcp";

/**
 * Every address the authorization server publishes, from `PUBLIC_APP_URL` only, never from the
 * request's `Host` (ADR 0009 section 3). `origin` is the parsed `PUBLIC_APP_URL`, which has no
 * trailing slash.
 */
export function mcpUrls(origin: string): {
  issuer: string;
  resource: string;
  protectedResourceMetadata: string;
  authorizationServerMetadata: string;
  authorize: string;
  token: string;
  register: string;
  revoke: string;
} {
  return {
    issuer: origin,
    resource: `${origin}${MCP_PATH}`,
    protectedResourceMetadata: `${origin}/.well-known/oauth-protected-resource${MCP_PATH}`,
    authorizationServerMetadata: `${origin}/.well-known/oauth-authorization-server`,
    authorize: `${origin}/mcp-oauth/authorize`,
    token: `${origin}/mcp-oauth/token`,
    register: `${origin}/mcp-oauth/register`,
    revoke: `${origin}/mcp-oauth/revoke`,
  };
}
